// Procurement Kit PDF Templates
// Static HTML templates for downloadable procurement documents
// All include "CONFIDENTIAL — US SOCCER PILOT" watermark

const now = new Date();
const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const baseStyles = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
    color: #1f2937; line-height: 1.7; font-size: 11px;
    padding: 50px 60px; max-width: 850px; margin: 0 auto; position: relative;
  }
  @media print { body { padding: 40px 50px; } @page { margin: 0.6in; } }
  .wm {
    position: fixed; top: 50%; left: 50%;
    transform: translate(-50%, -50%) rotate(-35deg);
    font-size: 44px; font-weight: 800; color: rgba(220,38,38,0.05);
    white-space: nowrap; pointer-events: none; z-index: 0; letter-spacing: 3px;
  }
  .hdr { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e3a5f; padding-bottom: 14px; margin-bottom: 28px; }
  .hdr-logo { font-size: 20px; font-weight: 700; color: #1e3a5f; }
  .hdr-logo span { color: #16a34a; }
  .hdr-meta { text-align: right; font-size: 9px; color: #6b7280; }
  .hdr-badge { display: inline-block; background: #fef3c7; color: #92400e; font-size: 8px; font-weight: 600; padding: 2px 8px; border-radius: 3px; margin-top: 4px; }
  h1 { font-size: 18px; color: #1e3a5f; margin-bottom: 6px; }
  h2 { font-size: 13px; color: #1e3a5f; margin: 22px 0 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
  h3 { font-size: 11px; color: #374151; margin: 14px 0 6px; }
  p, li { font-size: 10.5px; color: #4b5563; margin-bottom: 6px; }
  ul { padding-left: 18px; }
  li { margin-bottom: 4px; }
  .note { background: #f0fdf4; border-left: 3px solid #16a34a; padding: 10px 14px; font-size: 9.5px; color: #166534; margin: 16px 0; border-radius: 0 4px 4px 0; }
  .warn { background: #fffbeb; border-left: 3px solid #d97706; padding: 10px 14px; font-size: 9.5px; color: #92400e; margin: 16px 0; border-radius: 0 4px 4px 0; }
  .ftr { text-align: center; font-size: 8px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 14px; margin-top: 36px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 10px; }
  th { background: #f3f4f6; text-align: left; padding: 6px 10px; font-weight: 600; color: #374151; border-bottom: 1px solid #d1d5db; }
  td { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; color: #4b5563; }
`;

const header = (subtitle) => `
  <div class="wm">CONFIDENTIAL — US SOCCER PILOT</div>
  <div class="hdr">
    <div>
      <div class="hdr-logo"><span>YBV</span> Brand Intelligence</div>
      <div style="font-size:9px;color:#6b7280;margin-top:2px;">Procurement Kit — ${subtitle}</div>
    </div>
    <div class="hdr-meta">
      U.S. Soccer Federation<br/>
      ${dateStr}<br/>
      <span class="hdr-badge">CONFIDENTIAL</span>
    </div>
  </div>
`;

const footer = `
  <div class="ftr">
    CONFIDENTIAL — US SOCCER PILOT | YBV Brand Intelligence Platform | Generated ${now.toISOString()}<br/>
    This document is for authorized recipients only. Do not distribute without written approval.
  </div>
`;

// ── 1. Methodology & Data Sources ──

export const generateMethodologyPdf = () => `
<!DOCTYPE html><html><head>
<meta charset="UTF-8"><title>YBV Methodology & Data Sources</title>
<style>${baseStyles}</style>
</head><body>
${header('Methodology & Data Sources')}

<h1>Methodology & Data Sources</h1>
<p>Overview of the analytical framework, data inputs, and modeling approach used by the YBV Brand Intelligence Platform for the U.S. Soccer Federation pilot engagement.</p>

<h2>Valuation Framework</h2>
<p>The platform employs a proprietary brand valuation model aligned to published ISO 10668 principles, drawing on concepts from Interbrand and Brand Finance public methodologies. It is <strong>not</strong> a licensed implementation of either framework.</p>
<ul>
  <li><strong>Brand Strength Index (BSI-Aligned):</strong> Composite scoring across digital reach, media sentiment (modeled baseline), sponsorship portfolio depth, and competitive positioning. Scores are directional indicators.</li>
  <li><strong>Economic Value Range:</strong> Estimated brand value expressed as a range (low–high), derived from modeled revenue base, peer benchmarking, and growth trajectory. All values are estimated.</li>
  <li><strong>Relief-from-Royalty Reference:</strong> Planned module. Requires federation-supplied revenue projections, comparable royalty rates, and discount rate inputs. Not currently active.</li>
</ul>

<h2>Data Sources</h2>
<table>
  <tr><th>Category</th><th>Source</th><th>Status</th></tr>
  <tr><td>Social media metrics</td><td>Platform-verified follower counts (Instagram, X, TikTok, YouTube)</td><td>Manually verified</td></tr>
  <tr><td>Sponsorship portfolio</td><td>Publicly reported deals + modeled estimates</td><td>Modeled baseline</td></tr>
  <tr><td>Media sentiment</td><td>Modeled baseline score (NLP integration planned)</td><td>Planned integration</td></tr>
  <tr><td>FIFA rankings</td><td>Publicly available FIFA data</td><td>Current</td></tr>
  <tr><td>Revenue base</td><td>$85M/yr (est.) — not federation-reported</td><td>Modeled estimate</td></tr>
  <tr><td>Tournament scenarios</td><td>Internal probability model (48-team format)</td><td>Modeled</td></tr>
</table>

<div class="note">
  <strong>Key distinction:</strong> All current metrics are derived from the in-platform modeled dataset. No external live feeds are connected. Any future integrations will be explicitly documented and flagged.
</div>

<h2>Scenario Modeling</h2>
<p>World Cup outcome scenarios use a 6-stage probability distribution (Group Stage Exit through Championship) calibrated to the expanded 48-team 2026 format. Probabilities sum to 100% and are labeled as modeled estimates throughout the platform.</p>

<div class="warn">
  <strong>Disclaimer:</strong> All outputs are directional indicators for strategic planning purposes. They do not constitute certified financial forecasts or audited valuations.
</div>

${footer}
</body></html>
`;

// ── 2. Security & Privacy Overview ──

export const generateSecurityPdf = () => `
<!DOCTYPE html><html><head>
<meta charset="UTF-8"><title>YBV Security & Privacy Overview</title>
<style>${baseStyles}</style>
</head><body>
${header('Security & Privacy Overview')}

<h1>Security & Privacy Overview</h1>
<p>Summary of data handling, access controls, and privacy guardrails implemented in the YBV Brand Intelligence Platform for the U.S. Soccer Federation pilot.</p>

<h2>Tenant Isolation</h2>
<ul>
  <li>U.S. Soccer data is scoped to the authenticated portal context.</li>
  <li>Access is restricted via authenticated accounts and portal-level configuration.</li>
  <li>No cross-client private inputs are displayed or used in comparisons.</li>
  <li>All queries and exports are scoped to the authorized tenant.</li>
</ul>

<h2>Input Privacy</h2>
<ul>
  <li>Federation-supplied inputs are never surfaced in raw form.</li>
  <li>Private inputs remain hidden; only aggregated outputs are shown.</li>
  <li>Benchmarks shown are public or modeled baselines, clearly labeled.</li>
  <li>No raw financial, contractual, or internal data is exposed in any view or export.</li>
</ul>

<h2>Anti-Inference Formatting</h2>
<ul>
  <li>Outputs are aggregated and formatted with rounding and banding.</li>
  <li>Comparative views use bucketed ranges rather than precise figures to prevent reverse-engineering of underlying data points.</li>
  <li>Player concentration percentages are rounded to nearest 5%.</li>
</ul>

<h2>External Data Sources</h2>
<ul>
  <li>No external live feeds are connected unless explicitly integrated and documented.</li>
  <li>All current metrics are derived from the in-platform modeled dataset.</li>
  <li>Any future live integrations will be flagged in the data provenance layer and documented in this procurement kit.</li>
</ul>

<h2>Access Control</h2>
<table>
  <tr><th>Role</th><th>Capabilities</th><th>Assignment</th></tr>
  <tr><td>ADMIN</td><td>Full platform access, user management, invite capability, dataset mode control</td><td>YBV internal</td></tr>
  <tr><td>VIEWER</td><td>Read-only access to portal, dashboards, exports</td><td>Invite-only by ADMIN</td></tr>
</table>

<h2>PDF Exports</h2>
<ul>
  <li>All exported documents include "CONFIDENTIAL — US SOCCER PILOT" watermark.</li>
  <li>Generation timestamp is embedded in every export footer.</li>
  <li>Exports are scoped to the authenticated tenant context.</li>
</ul>

<div class="warn">
  <strong>Note:</strong> This is a pilot-phase deployment. Enterprise-grade infrastructure certifications (SOC 2, ISO 27001, etc.) are not in scope for the current engagement. Contact your YBV representative for production security roadmap details.
</div>

${footer}
</body></html>
`;

// ── 3. Implementation Plan ──

export const generateImplementationPdf = () => `
<!DOCTYPE html><html><head>
<meta charset="UTF-8"><title>YBV Implementation Plan</title>
<style>${baseStyles}</style>
</head><body>
${header('Implementation Plan')}

<h1>Implementation Plan</h1>
<p>Phased roadmap for the YBV Brand Intelligence Platform deployment for the U.S. Soccer Federation, from pilot through production readiness.</p>

<h2>Phase 1: Pilot (Current)</h2>
<h3>Duration: 8–12 weeks</h3>
<table>
  <tr><th>Deliverable</th><th>Status</th></tr>
  <tr><td>US Soccer Portal with USMNT + USWNT brand intelligence</td><td>Delivered</td></tr>
  <tr><td>Executive Dashboard with scenario modeling (6 outcomes, 48-team format)</td><td>Delivered</td></tr>
  <tr><td>ROI Simulator with tournament financial projections (modeled)</td><td>Delivered</td></tr>
  <tr><td>Competitive Intelligence benchmarking view</td><td>Delivered</td></tr>
  <tr><td>Brand Dependency risk analysis</td><td>Delivered</td></tr>
  <tr><td>Coaching intelligence profiles (Pochettino, Perez)</td><td>Delivered</td></tr>
  <tr><td>PDF export suite with confidentiality watermarks</td><td>Delivered</td></tr>
  <tr><td>Authentication (email/password, ADMIN/VIEWER roles)</td><td>Delivered</td></tr>
  <tr><td>Security & Privacy guardrails</td><td>Delivered</td></tr>
  <tr><td>Procurement Kit documentation</td><td>Delivered</td></tr>
</table>

<h2>Phase 2: Data Integration</h2>
<h3>Duration: 4–6 weeks post-pilot approval</h3>
<ul>
  <li>Connect verified social media APIs for automated metric refresh</li>
  <li>Integrate NLP-based sentiment analysis (replacing modeled baselines)</li>
  <li>Onboard federation-supplied revenue data for Relief-from-Royalty module</li>
  <li>Implement real-time alert thresholds for coaching profiles</li>
</ul>

<h2>Phase 3: Scale & Expand</h2>
<h3>Duration: 6–8 weeks post-Phase 2</h3>
<ul>
  <li>Expand to Clubs & Federations module (MLS clubs, peer federations)</li>
  <li>Launch Leagues module for cross-league benchmarking</li>
  <li>Implement profile comparison feature for athletes and coaches</li>
  <li>Add multi-federation tenant support</li>
</ul>

<h2>Phase 4: Production Hardening</h2>
<h3>Duration: Ongoing</h3>
<ul>
  <li>Enterprise SSO integration (SAML/OIDC)</li>
  <li>Audit logging and compliance reporting</li>
  <li>Infrastructure certification roadmap (SOC 2 Type II)</li>
  <li>SLA and uptime guarantees</li>
  <li>Dedicated support channel</li>
</ul>

<div class="note">
  <strong>Dependencies:</strong> Phase 2 onward requires federation data access agreements and API credentials. Timeline estimates are directional and subject to mutual availability.
</div>

${footer}
</body></html>
`;

// ── 4. Pilot vs Production Scope ──

export const generateScopePdf = () => `
<!DOCTYPE html><html><head>
<meta charset="UTF-8"><title>YBV Pilot vs Production Scope</title>
<style>${baseStyles}</style>
</head><body>
${header('Pilot vs Production Scope')}

<h1>Pilot vs Production Scope</h1>
<p>Comparison of capabilities available in the current pilot engagement versus the planned production deployment.</p>

<table>
  <tr><th>Capability</th><th>Pilot (Current)</th><th>Production</th></tr>
  <tr><td>Brand valuation model</td><td>BSI-Aligned + Interbrand-Aligned (modeled baselines)</td><td>+ Federation-calibrated inputs</td></tr>
  <tr><td>Social media data</td><td>Manually verified, periodic updates</td><td>Automated API refresh (daily)</td></tr>
  <tr><td>Sentiment analysis</td><td>Modeled baseline scores</td><td>Live NLP integration across platforms</td></tr>
  <tr><td>Revenue base</td><td>$85M/yr (est., modeled)</td><td>Federation-reported actuals</td></tr>
  <tr><td>Relief-from-Royalty</td><td>Planned (not active)</td><td>Active with federation inputs</td></tr>
  <tr><td>Tournament scenarios</td><td>6-stage model, 48-team format</td><td>Same + historical calibration</td></tr>
  <tr><td>Coaching profiles</td><td>2 profiles (Pochettino, Perez)</td><td>Full coaching staff + opponents</td></tr>
  <tr><td>Player tracking</td><td>52 players across USMNT + USWNT</td><td>Expanded roster + youth pipeline</td></tr>
  <tr><td>Clubs & Federations</td><td>Not available</td><td>MLS clubs + peer federations</td></tr>
  <tr><td>Leagues module</td><td>Not available</td><td>Cross-league benchmarking</td></tr>
  <tr><td>Authentication</td><td>Email/password, ADMIN/VIEWER</td><td>+ Enterprise SSO (SAML/OIDC)</td></tr>
  <tr><td>Audit logging</td><td>Not available</td><td>Full activity log + compliance</td></tr>
  <tr><td>Infrastructure</td><td>Pilot environment</td><td>Production-grade + SOC 2 roadmap</td></tr>
  <tr><td>PDF exports</td><td>All dashboards + procurement kit</td><td>Same + scheduled delivery</td></tr>
  <tr><td>Data refresh</td><td>Manual</td><td>Automated with provenance tracking</td></tr>
  <tr><td>SLA</td><td>Best-effort</td><td>Contractual uptime guarantee</td></tr>
</table>

<div class="note">
  <strong>Pilot value:</strong> The pilot provides a fully functional strategic intelligence surface using modeled baselines. Production upgrades focus on live data integration, scale, and enterprise infrastructure — the analytical framework and insights layer remain consistent.
</div>

<div class="warn">
  <strong>Disclaimer:</strong> Production scope and timeline are subject to mutual agreement. Features listed under "Production" represent planned capabilities, not contractual commitments. All modeled figures in the pilot are directional estimates.
</div>

${footer}
</body></html>
`;
